import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { PAGE_SIZE_OPTIONS } from '../../../shared/components/ui/pretty-select/common-options';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { rsql, rsqlTextSearch } from '../../../shared/helpers/rsql';
import { OptionsService } from '../../../shared/services/options.service';
import {
  OPTION_CATEGORY_LABEL,
  OPTION_CATEGORY_ORDER,
  OptionCategory,
  VehicleOption,
} from '../../../shared/types/option.types';

const SEARCH_FIELDS = ['nameFr', 'nameEn', 'slug'] as const;

const CATEGORY_OPTIONS: PrettySelectOption<OptionCategory | null>[] =
  OPTION_CATEGORY_ORDER.map((c) => ({ value: c, label: OPTION_CATEGORY_LABEL[c] }));

@Component({
  selector: 'app-options-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    PageHeaderComponent,
    DataStateComponent,
    PrettySelectComponent,
  ],
  templateUrl: './options-list.component.html',
})
export class OptionsListComponent implements OnInit {
  private readonly service = inject(OptionsService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly items = signal<VehicleOption[]>([]);
  readonly totalItems = signal(0);
  readonly totalPages = signal(0);
  readonly page = signal(1);
  readonly size = signal(10);

  readonly searchCtrl = this.fb.control('');
  readonly categoryCtrl = this.fb.control<OptionCategory | null>(null);

  readonly categoryOptions = CATEGORY_OPTIONS;
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly catLabel = OPTION_CATEGORY_LABEL;

  readonly pageWindow = computed<number[]>(() => {
    const total = this.totalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    const current = this.page();
    const window = 5;
    let start = Math.max(1, current - Math.floor(window / 2));
    const end = Math.min(total, start + window - 1);
    start = Math.max(1, end - window + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.page.set(1);
        this.load();
      });

    this.categoryCtrl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.page.set(1);
        this.load();
      });

    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const search = rsql.and(
      rsqlTextSearch(SEARCH_FIELDS, this.searchCtrl.value),
      rsql.eq('category', this.categoryCtrl.value),
    );
    this.service
      .list({
        page: this.page(),
        size: this.size(),
        search: search ?? undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.items.set(res.data ?? []);
          this.totalItems.set(res.totalItems ?? 0);
          this.totalPages.set(res.totalPages ?? 0);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.items.set([]);
          this.totalItems.set(0);
          this.totalPages.set(0);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages() || p === this.page()) return;
    this.page.set(p);
    this.load();
  }

  changeSize(s: number): void {
    if (!s || s === this.size()) return;
    this.size.set(s);
    this.page.set(1);
    this.load();
  }
}
